import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Sparkles, AlertCircle, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatbot } from '@/components/ChatbotProvider';

/**
 * Typing indicator with animated dots.
 */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-2">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 bg-sapphire-400 rounded-full"
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

/**
 * Chat message bubble component.
 */
function MessageBubble({ role, content }: { role: 'user' | 'assistant'; content: string }) {
  const isUser = role === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex', isUser ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed',
          isUser
            ? 'bg-sapphire-600 text-white rounded-br-md'
            : 'bg-white/10 text-gray-100 rounded-bl-md border border-white/10'
        )}
      >
        {content || <TypingIndicator />}
      </div>
    </motion.div>
  );
}

import { ChatCube } from './ChatCube';

/**
 * Floating AI chatbot widget.
 *
 * Features:
 * - Glassmorphism design with sapphire accents
 * - Collapsible bubble → full chat panel
 * - Animated typing indicator
 * - Mobile-responsive (full-screen on mobile)
 * - Auto-scroll to latest message
 */
export function AIChatbot() {
  const { isVisible, isOpen, toggleOpen, messages, isLoading, error, sendMessage, clearMessages } =
    useChatbot();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!input.trim() || isLoading) return;

      const message = input;
      setInput('');
      await sendMessage(message);
    },
    [input, isLoading, sendMessage]
  );

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-4">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'w-[calc(100vw-48px)] sm:w-[380px] max-h-[70vh] sm:max-h-[500px]',
              'bg-obsidian-900/90 backdrop-blur-xl',
              'border border-white/10 rounded-2xl shadow-2xl',
              'flex flex-col overflow-hidden',
              // Mobile full-screen adjustments - bottom positioned above the cube
              'max-sm:fixed max-sm:top-4 max-sm:left-4 max-sm:right-4 max-sm:bottom-20 max-sm:w-auto max-sm:max-h-none max-sm:rounded-xl'
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-linear-to-r from-sapphire-900/50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-8 h-8 rounded-lg bg-linear-to-br from-sapphire-500 to-sapphire-700 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <motion.div
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-obsidian-900"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Kivo Assistant</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                    AI-Powered Support
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <button
                    onClick={clearMessages}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Clear chat"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={toggleOpen}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Close chat"
                  title="Close chat"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px]">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-4">
                  <div className="w-12 h-12 rounded-xl bg-linear-to-br from-sapphire-500/20 to-sapphire-700/20 flex items-center justify-center mb-4 border border-sapphire-500/30">
                    <Sparkles className="w-6 h-6 text-sapphire-400" />
                  </div>
                  <p className="text-sm text-gray-300 mb-1">Hi! I'm Kivo 👋</p>
                  <p className="text-xs text-gray-500 max-w-[200px]">
                    Ask me about our services, projects, or how we can help your business.
                  </p>
                </div>
              ) : (
                messages.map((msg) => (
                  <MessageBubble key={msg.id} role={msg.role} content={msg.content} />
                ))
              )}
              {isLoading && messages[messages.length - 1]?.content === '' && null}
              <div ref={messagesEndRef} />
            </div>

            {/* Error */}
            {error && (
              <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20">
                <div className="flex items-center gap-2 text-xs text-red-400">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-3 py-2 border border-white/10 focus-within:border-sapphire-500/50 transition-colors">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about our services..."
                  disabled={isLoading}
                  maxLength={500}
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 outline-none disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isLoading}
                  aria-label="Send message"
                  title="Send message"
                  className={cn(
                    'p-2 rounded-lg transition-all',
                    input.trim() && !isLoading
                      ? 'bg-sapphire-600 text-white hover:bg-sapphire-500'
                      : 'bg-white/5 text-gray-500 cursor-not-allowed'
                  )}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[10px] text-gray-600 text-center mt-2">
                Powered by AI • {500 - input.length} chars left
              </p>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <ChatCube isOpen={isOpen} toggleOpen={toggleOpen} />
    </div>
  );
}

export default AIChatbot;
